import { PACKAGE_TYPE } from "constants/packageOptions.constants";
import { DEFAULT_PALLET_OPTIONS, PALLET_PRESETS, PALLET_TYPE } from "constants/palletOptions.constants";
import { UNIT_SYSTEM } from "constants/units.contants";
import ExcelJS from "exceljs";
import { CargoRow, Group, InputNumber, PalletRow } from "models/packingList";
import { Unit } from "models/units";
import { createDefaultPackageSettings, createDefaultPalletRow } from "store/reducers/packingList/packingList.helpers";
import { getRandomPaletteColor } from "./color.utils";
import { ValueOf } from "./typeCheck.utils";
import { fromDisplayLength, fromDisplayWeight } from "./units.utils";

type RawRow = {
  group: string | null;
  product: string | null;
  type: string | null;
  lengthOrDiameter: InputNumber;
  width: InputNumber;
  height: InputNumber;
  weight: InputNumber;
  quantity: InputNumber;
  palletMaxWeight: InputNumber;
  palletDepth: InputNumber;
  units: string | null;
};

type TextCellMap = Record<number, "group" | "product" | "type" | "units">;

type NumberCellMap = Record<
  number,
  "lengthOrDiameter" | "width" | "height" | "weight" | "quantity" | "palletMaxWeight" | "palletDepth"
>;

type PackageType = ValueOf<typeof PACKAGE_TYPE>;

const createEmptyRawRow = (): RawRow => ({
  group: null,
  product: null,
  type: null,
  lengthOrDiameter: null,
  width: null,
  height: null,
  weight: null,
  quantity: null,
  palletMaxWeight: null,
  palletDepth: null,
  units: null,
});

const TEXT_CELL_MAP: TextCellMap = {
  1: "group",
  2: "product",
  3: "type",
  11: "units",
};

const NUMBER_CELL_MAP: NumberCellMap = {
  4: "lengthOrDiameter",
  5: "width",
  6: "height",
  7: "weight",
  8: "quantity",
  9: "palletMaxWeight",
  10: "palletDepth",
};

const UNIT_SYSTEM_ALIASES: Record<string, Unit> = {
  si: UNIT_SYSTEM.METRIC,
  us: UNIT_SYSTEM.IMPERIAL,
};

const PALLET_PRESET_NAME_TO_TYPE: Record<string, string> = Object.fromEntries(
  DEFAULT_PALLET_OPTIONS.filter((option) => option.value !== PALLET_TYPE.CUSTOM_PALLETS).map((option) => [
    option.label.trim().toLowerCase(),
    option.value,
  ])
);

const PACKAGE_TYPE_ALIASES: Record<string, PackageType> = {
  box: PACKAGE_TYPE.BOX,
  bigbags: PACKAGE_TYPE.BIG_BAGS,
  "big bags": PACKAGE_TYPE.BIG_BAGS,
  "big-bags": PACKAGE_TYPE.BIG_BAGS,
  barrels: PACKAGE_TYPE.BARRELS,
  barrel: PACKAGE_TYPE.BARRELS,
  sacks: PACKAGE_TYPE.SACKS,
  sack: PACKAGE_TYPE.SACKS,
  pipes: PACKAGE_TYPE.PIPES,
  pipe: PACKAGE_TYPE.PIPES,
  rolls: PACKAGE_TYPE.ROLLS,
  roll: PACKAGE_TYPE.ROLLS,
};

const readFileAsync = (file: Blob) => {
  const reader = new FileReader();
  return new Promise<ArrayBuffer>((resolve, reject) => {
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(new Error("Error reading file"));
    reader.readAsArrayBuffer(file);
  });
};

const toText = (value: ExcelJS.CellValue): string | null => {
  if (typeof value === "string") return value.trim() || null;
  if (typeof value === "number") return String(value);
  return null;
};

const toNumber = (value: ExcelJS.CellValue): InputNumber => {
  if (typeof value === "number") return value;
  if (typeof value === "string") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const parsePackageType = (type: string | null): PackageType => {
  if (!type) return PACKAGE_TYPE.BOX;
  return PACKAGE_TYPE_ALIASES[type.trim().toLowerCase()] ?? PACKAGE_TYPE.BOX;
};

const parseRows = async (file: File): Promise<RawRow[]> => {
  const workbook = new ExcelJS.Workbook();
  const arrayBuffer = await readFileAsync(file);
  await workbook.xlsx.load(arrayBuffer);

  const worksheet = workbook.worksheets[0];
  const rows: RawRow[] = [];

  worksheet?.eachRow((row, rowNumber) => {
    if (rowNumber === 1) return; // header row

    const rawRow = createEmptyRawRow();
    row.eachCell((cell, colNumber) => {
      const textKey = TEXT_CELL_MAP[colNumber];
      if (textKey) {
        rawRow[textKey] = toText(cell.value);
        return;
      }

      const numberKey = NUMBER_CELL_MAP[colNumber];
      if (numberKey) rawRow[numberKey] = toNumber(cell.value);
    });

    rows.push(rawRow);
  });

  return rows;
};

const createImportedPalletRow = (row: RawRow, unitSystem: Unit): PalletRow => {
  const presetType = row.product ? PALLET_PRESET_NAME_TO_TYPE[row.product.trim().toLowerCase()] : undefined;

  if (presetType) {
    return {
      id: crypto.randomUUID(),
      palletType: presetType,
      ...PALLET_PRESETS[presetType],
      color: getRandomPaletteColor(),
    };
  }

  return {
    id: crypto.randomUUID(),
    palletType: PALLET_TYPE.CUSTOM_PALLETS,
    palleteName: row.product ?? undefined,
    length: fromDisplayLength(row.lengthOrDiameter, unitSystem),
    width: fromDisplayLength(row.width, unitSystem),
    height: fromDisplayLength(row.height, unitSystem),
    depth: fromDisplayLength(row.palletDepth, unitSystem),
    maxWeight: fromDisplayWeight(row.palletMaxWeight, unitSystem),
    color: getRandomPaletteColor(),
  };
};

const buildGroups = (rows: RawRow[], unitSystem: Unit): Group[] => {
  const groups: Group[] = [];

  rows.forEach((row) => {
    if (row.group) {
      const hasPallet = Boolean(row.palletMaxWeight && row.palletDepth);

      groups.push({
        id: crypto.randomUUID(),
        name: row.group,
        usePallets: hasPallet,
        cargoRows: [],
        palletRow: hasPallet ? createImportedPalletRow(row, unitSystem) : createDefaultPalletRow(),
      });
      return;
    }

    const currentGroup = groups[groups.length - 1];
    if (!currentGroup || !row.product) return;

    const packageType = parsePackageType(row.type);

    const cargoRow: CargoRow = {
      id: crypto.randomUUID(),
      product: row.product,
      packageType,
      length: fromDisplayLength(row.lengthOrDiameter, unitSystem),
      width: fromDisplayLength(row.width, unitSystem),
      height: fromDisplayLength(row.height, unitSystem),
      weight: fromDisplayWeight(row.weight, unitSystem),
      qty: row.quantity,
      color: getRandomPaletteColor(),
      packageSettings: createDefaultPackageSettings(packageType),
    };

    currentGroup.cargoRows.push(cargoRow);
  });

  return groups;
};

const parseUnitSystem = (rows: RawRow[]): Unit | null => {
  const units = rows.find((row) => row.units)?.units;
  if (!units) return null;
  return UNIT_SYSTEM_ALIASES[units.trim().toLowerCase()] ?? null;
};

export const importPackingListXlsx = async (file: File): Promise<{ groups: Group[]; unitSystem: Unit | null }> => {
  const rows = await parseRows(file);
  const unitSystem = parseUnitSystem(rows);

  return { groups: buildGroups(rows, unitSystem ?? UNIT_SYSTEM.METRIC), unitSystem };
};
